import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Box,
  styled,
  LinearProgress,
  Paper
} from '@mui/material';
import ProfilePicture from '../../../components/doctor/profilepicture';
import PersonalInfo from '../../../components/doctor/personalinfo';
import ContactInfo from '../../../components/doctor/contactinfo';
import WorkExperience from '../../../components/doctor/work';
import Education from '../../../components/doctor/education';
import CertificationsLicenses from '../../../components/doctor/certificate';
import Availability from '../../../components/doctor/availability';
import ServicesOffered from '../../../components/doctor/services';
import { updateDoctorProfile } from '../../../redux/middleware/user/doctor/profile';
import { showToast } from './config/helpers';
import { toast } from 'react-toastify';
import { shades } from '../../../../theme';
import { Check, ArrowForward, ArrowBack, Send } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const CustomStepLabel = styled(StepLabel)(({ theme }) => ({
  '& .MuiStepLabel-label': {
    color: shades.neutral[500],
    fontSize: '0.9rem',
    transition: 'all 0.2s ease',
    '&.Mui-active': {
      color: 'rgb(0, 115, 255)',
      fontWeight: 600,
      fontSize: '1rem'
    },
    '&.Mui-completed': {
      color: shades.neutral[700]
    }
  },
  '& .MuiStepLabel-iconContainer': {
    '& .MuiStepIcon-root': {
      color: shades.neutral[300],
      transition: 'all 0.3s ease',
      '&.Mui-active': {
        color: 'rgb(0, 115, 255)',
        transform: 'scale(1.2)'
      },
      '&.Mui-completed': {
        color: 'rgb(0, 115, 255)'
      }
    }
  }
}));

const CustomStepper = styled(Stepper)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    borderColor: shades.neutral[200],
    transition: 'all 0.3s ease'
  },
  '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
    borderColor: 'rgb(0, 115, 255)',
    borderWidth: '2px'
  },
  '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
    borderColor: 'rgb(0, 115, 255)'
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: '8px 24px',
  borderRadius: '8px',
  transition: 'all 0.3s ease',
  textTransform: 'none',
  fontWeight: 600
}));

const steps = [
  'Profile Picture',
  'Personal Info',
  'Contact Info',
  'Experience',
  'Education',
  'Services Offered',
  'Certifications & Licenses',
  'Availability'
];

const ProfileCompletion = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [stepValidation, setStepValidation] = useState({
    0: false,
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
    6: false,
    7: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLastStep, setIsLastStep] = useState(false);
  const [formData, setFormData] = useState({
    profilePicture: null,
    personalInfo: {},
    contactInfo: {},
    workExperience: [],
    education: [],
    servicesOffered: [],
    certifications: [],
    availability: {}
  });

  useEffect(() => {
    const completedSteps = Object.values(stepValidation).filter(Boolean).length;
    setProgress((completedSteps / steps.length) * 100);
  }, [stepValidation]);

  useEffect(() => {
    setIsLastStep(activeStep === steps.length - 1);
  }, [activeStep]);

  const handleNext = () => {
    if (activeStep < steps.length - 1 && stepValidation[activeStep]) {
      setActiveStep((prevStep) => prevStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prevStep) => prevStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const setStepValid = (stepIndex, isValid) => {
    setStepValidation((prev) => ({
      ...prev,
      [stepIndex]: isValid
    }));
  };

  const updateFormData = (section, data) => {
    setFormData((prevData) => ({
      ...prevData,
      [section]: data
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await dispatch(updateDoctorProfile(formData));
      if (response.status >= 200 && response.status < 300) {
        // Clear the form
        setFormData({
          profilePicture: null,
          personalInfo: {},
          contactInfo: {},
          workExperience: [],
          education: [],
          servicesOffered: [],
          certifications: [],
          availability: {}
        });
        // Set pending approval flag in localStorage
        localStorage.setItem('pendingApproval', 'true');
        // Redirect to the dashboard (which will then redirect to pending approval if not verified)
        navigate('/app/doctor/dashboard');
      }
    } catch (error) {
      console.error('Error submitting profile:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <ProfilePicture
            onDataChange={(data) => updateFormData('profilePicture', data)}
            initialData={formData.profilePicture}
            onValidityChange={(isValid) => setStepValid(0, isValid)}
          />
        );
      case 1:
        return (
          <PersonalInfo
            onDataChange={(data) => updateFormData('personalInfo', data)}
            initialData={formData.personalInfo}
            onValidityChange={(isValid) => setStepValid(1, isValid)}
          />
        );
      case 2:
        return (
          <ContactInfo
            onDataChange={(data) => updateFormData('contactInfo', data)}
            initialData={formData.contactInfo}
            onValidityChange={(isValid) => setStepValid(2, isValid)}
          />
        );
      case 3:
        return (
          <WorkExperience
            onDataChange={(data) => updateFormData('workExperience', data)}
            initialData={formData.workExperience}
            onValidityChange={(isValid) => setStepValid(3, isValid)}
          />
        );
      case 4:
        return (
          <Education
            onDataChange={(data) => updateFormData('education', data)}
            initialData={formData.education}
            onValidityChange={(isValid) => setStepValid(4, isValid)}
          />
        );
      case 5:
        return (
          <ServicesOffered
            onDataChange={(data) => updateFormData('servicesOffered', data)}
            initialData={formData.servicesOffered}
            onValidityChange={(isValid) => setStepValid(5, isValid)}
          />
        );
      case 6:
        return (
          <CertificationsLicenses
            onDataChange={(data) => updateFormData('certifications', data)}
            initialData={formData.certifications}
            onValidityChange={(isValid) => setStepValid(6, isValid)}
          />
        );
      case 7:
        return (
          <Availability
            onDataChange={(data) => updateFormData('availability', data)}
            initialData={formData.availability}
            onValidityChange={(isValid) => setStepValid(7, isValid)}
          />
        );
      default:
        return (
          <Box
            sx={{
						  display: 'flex',
						  flexDirection: 'column',
						  justifyContent: 'center',
						  alignItems: 'center',
						  minHeight: '300px',
						  gap: 2
            }}
          >
            <Typography variant='h6' color={shades.neutral[600]}>
              Coming Soon
            </Typography>
            <Typography
              variant='body2'
              color={shades.neutral[500]}
              textAlign='center'
            >
              This section is under development. Please complete the available
              sections.
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Box
      sx={{
			  width: '100vw',
			  minHeight: { xs: 'calc(100vh - 64px)', md: 'min-content' },
			  marginTop: '10vh',
			  padding: { xs: '5% 3%', sm: '5%', md: '5% 8%' },
			  bgcolor: '#F8FAFC',
			  color: 'black'
      }}
    >
      {/* Progress Indicator */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant='body2' color={shades.neutral[600]}>
            Profile Completion
          </Typography>
          <Typography variant='body2' color={shades.neutral[600]}>
            {Math.round(progress)}%
          </Typography>
        </Box>
        <LinearProgress
          variant='determinate'
          value={progress}
          sx={{
					  height: 6,
					  borderRadius: 3,
					  bgcolor: shades.neutral[200],
					  '& .MuiLinearProgress-bar': { bgcolor: 'rgb(0, 115, 255)' }
          }}
        />
      </Box>

      <Typography
        variant='h5'
        fontWeight='bold'
        gutterBottom
        sx={{ color: shades.neutral[800], textAlign: 'center', mb: 4 }}
      >
        Complete Your Profile
      </Typography>

      <Paper
        elevation={0}
        sx={{
				  padding: { xs: 2, sm: 3, md: 4 },
				  borderRadius: 2,
				  mb: 4,
				  bgcolor: 'white',
				  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
        }}
      >
        <CustomStepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={index}>
              <CustomStepLabel>{label}</CustomStepLabel>
            </Step>
          ))}
        </CustomStepper>
      </Paper>

      <Paper
        elevation={0}
        sx={{
				  mt: 4,
				  minHeight: { xs: '350px', sm: '400px', md: '450px' },
				  backgroundColor: 'white',
				  borderRadius: 2,
				  overflow: 'hidden',
				  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
        }}
      >
        <Box
          sx={{
					  borderBottom: `1px solid ${shades.neutral[200]}`,
					  p: 2,
					  bgcolor: 'rgba(0, 115, 255, 0.02)'
          }}
        >
          <Typography variant='h6' color={shades.neutral[700]}>
            {steps[activeStep]}
          </Typography>
          <Typography variant='body2' color={shades.neutral[500]}>
            Step {activeStep + 1} of {steps.length}
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>{renderStepContent(activeStep)}</Box>
      </Paper>

      {isLastStep && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <StyledButton
            variant='contained'
            size='large'
            endIcon={<Send />}
            onClick={handleSubmit}
            disabled={!stepValidation[activeStep] || isSubmitting}
            sx={{
						  bgcolor: 'rgb(0, 115, 255)',
						  '&:hover': { bgcolor: 'rgb(0, 95, 210)' },
						  minWidth: '200px',
						  py: 1.5
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Profile'}
          </StyledButton>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <StyledButton
          variant='outlined'
          startIcon={<ArrowBack />}
          disabled={activeStep === 0}
          onClick={handleBack}
        >
          Back
        </StyledButton>

        {!isLastStep
          ? (
            <StyledButton
              variant='contained'
              endIcon={<ArrowForward />}
              onClick={handleNext}
              disabled={!stepValidation[activeStep]}
            >
              Next
            </StyledButton>
            )
          : (
            <StyledButton
              variant='contained'
              endIcon={<ArrowForward />}
              disabled
              sx={{ visibility: 'hidden' }}
            >
              Next
            </StyledButton>
            )}
      </Box>
    </Box>
  );
};

export default ProfileCompletion;
